﻿import { FC, useState, FormEvent, ChangeEvent } from 'react';
import { Container, Section, Card, CardTitle, CardDescription, Button, Input, Alert, Badge } from '../components/ui';
import { SEOHead } from '../components/seo';
import { ROUTES } from '../config/routes';
import { validateDownloadTokenFormat } from '../utils/validation';
import { trackProductDownload } from '../analytics/events';
import { PageProps } from './HomePage';
import { appConfig } from '../config/env';

interface AvailableDownload {
  id: string;
  fileName: string;
}

interface DownloadInfoResponse {
  success: boolean;
  data?: {
    productId: string;
    files: AvailableDownload[];
    expiresAt?: string;
    downloadsRemaining?: number;
  };
  error?: { code?: string; message?: string };
}

export const DownloadPage: FC<PageProps> = ({ onNavigate }) => {
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [files, setFiles] = useState<AvailableDownload[]>([]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setFiles([]);

    const cleanToken = token.trim();
    if (!cleanToken) {
      setError('Please enter your secure download access token.');
      return;
    }
    if (!validateDownloadTokenFormat(cleanToken)) {
      setError('The token format looks incorrect. Please check your confirmation email.');
      return;
    }

    setIsVerifying(true);
    try {
      const response = await fetch(
        `${appConfig.apiBaseUrl}/download-info?token=${encodeURIComponent(cleanToken)}`,
        { headers: { Accept: 'application/json' } },
      );
      const result = await response.json() as DownloadInfoResponse;

      if (!response.ok || !result.success || !result.data) {
        throw new Error(result.error?.message || 'Unable to verify this token. It may be expired, revoked, or unavailable.');
      }

      setFiles(result.data.files);
      setSuccess(true);
      trackProductDownload('toolkit_verification', cleanToken);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to contact the download service. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDownload = async (file: AvailableDownload) => {
    setError(null);
    setDownloadingFileId(file.id);

    try {
      const response = await fetch(
        `${appConfig.apiBaseUrl}/download?token=${encodeURIComponent(token.trim())}&fileId=${encodeURIComponent(file.id)}`,
      );

      if (!response.ok) {
        let message = `Download failed (HTTP ${response.status}).`;
        try {
          const result = await response.json() as { error?: { message?: string } };
          if (result.error?.message) message = result.error.message;
        } catch {
          // A non-JSON response may be returned for a failed download.
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = file.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
      trackProductDownload(file.id, token.trim());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'The file could not be downloaded. Please try again.');
    } finally {
      setDownloadingFileId(null);
    }
  };

  return (
    <>
      <SEOHead
        title="Download Center — Connect Source"
        description="Access and download your purchased digital toolkits and free resources using your secure delivery token."
        noIndex={true}
      />
      <Section
        title="Digital Deliverable Portal"
        subtitle="Verify your delivery token to access your authorized files."
        style={{ padding: '4.5rem 0' }}
      >
        <Container size="md">
          <Card style={{ padding: '2.5rem' }}>
            <Badge variant="teal" style={{ marginBottom: '1rem' }}>Secure Download Access</Badge>
            <CardTitle style={{ fontSize: '1.4rem' }}>Enter Download Token</CardTitle>
            <CardDescription style={{ marginTop: '0.5rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              Enter the token from your order confirmation or free-guide email. Our server checks access before delivering files.
            </CardDescription>

            {error && <Alert variant="error" title="Download Portal">{error}</Alert>}
            {success && (
              <Alert variant="success" title="Token Verified">
                Your token has been verified by the server. The files available to this token are listed below.
              </Alert>
            )}

            <form onSubmit={handleVerify} noValidate>
              <div style={{ marginBottom: '1.25rem' }}>
                <Input
                  id="download-token"
                  label="Download Access Token *"
                  placeholder="Paste your download token"
                  value={token}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setToken(e.target.value)}
                  helperText="Find this token in your order confirmation or guide email."
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <Button type="submit" variant="primary" size="md" disabled={isVerifying}>
                  {isVerifying ? 'Verifying…' : 'Verify & Access Files →'}
                </Button>
                <Button type="button" variant="outline" size="md" onClick={() => onNavigate(ROUTES.HOME)}>
                  Return Home
                </Button>
              </div>
            </form>

            {success && (
              <div style={{ marginTop: '1.5rem' }}>
                <CardTitle style={{ fontSize: '1.1rem' }}>Available files ({files.length})</CardTitle>
                {files.length === 0 ? (
                  <CardDescription>No files are currently available for this token. Please contact support.</CardDescription>
                ) : (
                  <div style={{ display: 'grid', gap: '0.75rem', marginTop: '1rem' }}>
                    {files.map((file) => (
                      <div key={file.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '1rem', border: '1px solid var(--color-border-subtle)', borderRadius: '8px', flexWrap: 'wrap' }}>
                        <span>{file.fileName}</span>
                        <Button
                          type="button"
                          variant="primary"
                          size="md"
                          disabled={downloadingFileId !== null}
                          onClick={() => void handleDownload(file)}
                        >
                          {downloadingFileId === file.id ? 'Preparing…' : 'Download'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-border-subtle)', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
              Need assistance with an existing order? Contact support at info@connectsource.co.in.
            </div>
          </Card>
        </Container>
      </Section>
    </>
  );
};
